import React, { useState, useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import Hero from "./components/sections/Hero";
import WhatIsKanonics from "./components/sections/WhatIsKanonics";
import Services from "./components/sections/Services";
import TrustedBy from "./components/sections/TrustedBy";
import Testimonials from "./components/sections/Testimonials";
import Capabilities from "./components/sections/Capabilities";
import DigitalTransformation from "./components/sections/DigitalTransformation";
import FAQ from "./components/sections/FAQ";
import ContactSection from "./components/sections/ContactSection";
import CookieConsent from "./components/ui/CookieConsent";
import ScrollToTop from "./utils/ScrollToTop";
import LocaleGuard from "./components/layout/LocaleGuard";
import SeoHead from "./components/ui/SeoHead";
import LoadingScreen from "./components/ui/LoadingScreen";
import ErrorBoundary from "./components/ui/ErrorBoundary";
import PageLoader from "./components/ui/PageLoader";
import { ModalProvider, useModalContext } from "./contexts/ModalContext";

const Impressum = lazy(() => import("./pages/Impressum"));
const DataSecurity = lazy(() => import("./pages/DataSecurity"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const Contact = lazy(() => import("./pages/Contact"));

const isHomePath = () => {
  const path = window.location.pathname;
  return path === "/" || /^\/(en|de)\/?$/.test(path);
};

const AppContent: React.FC = () => {
  const { openModals } = useModalContext();
  const [isBackNavigation, setIsBackNavigation] = useState(false);
  // Initialize loading to true only for home page
  const [loading, setLoading] = useState(() => isHomePath());
  const [heroReady, setHeroReady] = useState(() => !isHomePath());

  // Detect back/forward button navigation
  useEffect(() => {
    const handlePopState = () => {
      // If there are open modals, don't navigate back
      // The modal's popstate handler will close it
      if (openModals.size === 0) {
        setIsBackNavigation(true);
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [openModals]);

  // Immediately hide loading if not on home page or back navigation
  useEffect(() => {
    if (!isHomePath() || isBackNavigation) {
      setLoading(false);
      if (isBackNavigation) {
        const timer = setTimeout(() => setIsBackNavigation(false), 50);
        return () => clearTimeout(timer);
      }
    }
  }, [isBackNavigation]);

  // Show loading only on home page (unless back navigation)
  useEffect(() => {
    if (!isHomePath() || isBackNavigation) {
      setLoading(false);
      setHeroReady(true);
      return;
    }
    const minTime = new Promise<void>(res => setTimeout(res, 1800));
    const pageLoad = new Promise<void>(res => {
      if (document.readyState === "complete") res();
      else window.addEventListener("load", () => res(), { once: true });
    });
    Promise.all([minTime, pageLoad]).then(() => {
      window.scrollTo(0, 0);
      setLoading(false);
      setHeroReady(true);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Router>
      <LoadingScreen isVisible={loading} />
      <SeoHead />
      <ScrollToTop />
      <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <Navbar />
        <main style={{ flex: 1 }}>
          <Suspense fallback={null}>
            <Routes>
            {/* Redirect bare root to default locale */}
            <Route path="/" element={<Navigate to="/en" replace />} />

            {/* All locale-prefixed routes */}
            <Route path=":lang" element={<LocaleGuard />}>
              <Route index element={<MainLayout heroReady={heroReady} onMount={() => setHeroReady(true)} />} />
              <Route path="impressum" element={<Impressum />} />
              <Route path="data-security" element={<DataSecurity />} />
              <Route path="projects/:slug" element={<Suspense fallback={<PageLoader />}><ProjectDetail /></Suspense>} />
              <Route path="contact" element={<Contact />} />
            </Route>

            {/* Catch-all → default locale */}
            <Route path="*" element={<Navigate to="/en" replace />} />
          </Routes>
          </Suspense>
        </main>
        <CookieConsent />
        <Footer />
      </div>
    </Router>
  );
};

const MainLayout: React.FC<{ heroReady: boolean; onMount: () => void }> = ({ heroReady, onMount }) => {
  useEffect(() => { onMount(); }, [onMount]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <Hero heroReady={heroReady} />
      <WhatIsKanonics />
      <Services />
      <DigitalTransformation />
      <Testimonials />
      <Capabilities />
      <TrustedBy />
      <FAQ />
      <ContactSection />
    </>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ModalProvider>
        <AppContent />
      </ModalProvider>
    </ErrorBoundary>
  );
};

export default App;
